import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Check, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface StringListEditorProps {
  title: string;
  items: { id: string; value: string }[];
  isLoading?: boolean;
  onAdd: (value: string) => void;
  onUpdate: (id: string, value: string) => void;
  onDelete: (id: string) => void;
  isAdding?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

export default function StringListEditor({
  title,
  items,
  isLoading,
  onAdd,
  onUpdate,
  onDelete,
  isAdding,
  isUpdating,
  isDeleting,
}: StringListEditorProps) {
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const lower = search.toLowerCase();
    return items.filter((item) => item.value.toLowerCase().includes(lower));
  }, [items, search]);

  const handleAdd = () => {
    if (!newValue.trim()) return;
    onAdd(newValue.trim());
    setNewValue('');
  };

  const handleStartEdit = (id: string, value: string) => {
    setEditingId(id);
    setEditValue(value);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editValue.trim()) return;
    onUpdate(editingId, editValue.trim());
    setEditingId(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleConfirmDelete = (id: string) => {
    onDelete(id);
    setDeletingId(null);
  };

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{title}</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{title}</h2>

      {/* Add new item */}
      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Add new item..."
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="max-w-sm"
        />
        <Button onClick={handleAdd} disabled={!newValue.trim() || isAdding} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Search */}
      {items.length > 10 && (
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Filter list..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      <p className="text-sm text-gray-500 mb-3">
        {items.length} item{items.length !== 1 ? 's' : ''}
        {search && ` (${filteredItems.length} shown)`}
      </p>

      {/* Items list */}
      <div className="border rounded-lg divide-y">
        {filteredItems.map((item) => (
          <div key={item.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
            {editingId === item.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  className="max-w-sm h-8"
                  autoFocus
                />
                <Button size="sm" variant="ghost" onClick={handleSaveEdit} disabled={!editValue.trim() || isUpdating}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : deletingId === item.id ? (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm text-red-600">Delete "{item.value}"?</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleConfirmDelete(item.id)}
                  disabled={isDeleting}
                >
                  Confirm
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <span className="text-sm text-gray-900">{item.value}</span>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleStartEdit(item.id, item.value)}
                    className="h-7 w-7 p-0"
                  >
                    <Pencil className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeletingId(item.id)}
                    className="h-7 w-7 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            {search ? 'No items match your filter.' : 'No items yet. Add one above.'}
          </div>
        )}
      </div>
    </div>
  );
}
